import { useEffect, useRef } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { Location } from 'history';
import { addDatadogRumAction } from '../../lib/datadogRum';
import { findRouteKeyFor } from '../../routers';
import { restoreScrollPosition, RestoreResult } from './restoreScrollPosition';
import {
  CELL_SELECTOR,
  captureScrollPosition,
  getScrollPosition,
  keyFor,
  saveScrollPosition,
  ScrollPosition
} from './scrollPositions';

const isBackForwardOrReload = () => {
  const [navigation] = (performance.getEntriesByType?.('navigation') ??
    []) as PerformanceNavigationTiming[];
  return navigation?.type === 'back_forward' || navigation?.type === 'reload';
};

const isGiftFinderTabChange = (from: Location, to: Location) =>
  findRouteKeyFor(from.pathname) === 'GiftFinderPage' &&
  findRouteKeyFor(to.pathname) === 'GiftFinderPage';

const restore = (position: ScrollPosition) =>
  restoreScrollPosition(position, (result: RestoreResult, durationMs: number) =>
    addDatadogRumAction('scroll_restoration', {
      result,
      duration_ms: durationMs,
      has_anchor: Boolean(position.anchor),
      clicked_anchor: Boolean(position.anchor?.clicked)
    })
  );

export const ScrollRestoration: React.FC = () => {
  const history = useHistory();
  const location = useLocation();
  const renderedLocation = useRef(location);
  const clickedVariantId = useRef<string | undefined>(undefined);

  useEffect(() => {
    window.history.scrollRestoration = 'manual';

    let previousLocation = history.location;

    const save = (location: Location) => {
      saveScrollPosition(keyFor(location), captureScrollPosition(clickedVariantId.current));
      clickedVariantId.current = undefined;
    };

    const handleClick = (event: MouseEvent) => {
      const opensInThisTab =
        event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey;
      const link = opensInThisTab
        ? (event.target as Element | null)?.closest?.('a[href]')
        : undefined;
      const cell = link?.closest(CELL_SELECTOR);
      clickedVariantId.current = cell?.getAttribute('data-variant-pk') ?? undefined;
    };

    const handlePageHide = () => save(history.location);

    const unlisten = history.listen(location => {
      save(previousLocation);
      previousLocation = location;
    });

    document.addEventListener('click', handleClick, true);
    window.addEventListener('pagehide', handlePageHide);

    return () => {
      unlisten();
      document.removeEventListener('click', handleClick, true);
      window.removeEventListener('pagehide', handlePageHide);
    };
  }, [history]);

  useEffect(() => {
    const previous = renderedLocation.current;
    renderedLocation.current = location;

    if (previous === location) {
      const position = isBackForwardOrReload() && getScrollPosition(keyFor(location));
      return position ? restore(position) : undefined;
    }

    if (history.action === 'POP') {
      const position = getScrollPosition(keyFor(location));
      if (position) return restore(position);
      window.scrollTo(0, 0);
      return;
    }

    if (previous.pathname === location.pathname || isGiftFinderTabChange(previous, location))
      return;

    window.scrollTo(0, 0);
  }, [history, location]);

  return null;
};
